import React, { useContext, useState, useEffect, useRef } from "react";
import axios from "../context/axios";
import { AuthContext } from "../context/AuthContext";
import { MDBSpinner } from "mdb-react-ui-kit";

/* ─── Keyframe injection ─── */
if (typeof document !== "undefined" && !document.getElementById("notif-keyframes")) {
  const style = document.createElement("style");
  style.id = "notif-keyframes";
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-14px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes badgePop {
      from { transform: scale(0.4); opacity: 0; }
      to   { transform: scale(1); opacity: 1; }
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .notif-card:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 16px rgba(17,24,39,0.09);
    }
    .notif-markall:hover { background: rgba(99,102,241,0.08) !important; }
    .notif-list::-webkit-scrollbar { width: 4px; }
    .notif-list::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
  `;
  document.head.appendChild(style);
}

/* ─── Type config ─── */
const typeConfig = {
  reporte: {
    icon: "⏸️",
    label: "Reportée",
    cardBg: "rgba(251,191,36,0.07)",
    border: "rgba(251,191,36,0.45)",
    iconBg: "rgba(251,191,36,0.15)",
    labelColor: "#b45309",
    labelBg: "rgba(251,191,36,0.18)",
    accent: "#f59e0b",
    tabBg: "rgba(251,191,36,0.12)",
    tabText: "#92400e",
  },
  confirmer: {
    icon: "✅",
    label: "À confirmer",
    cardBg: "rgba(99,102,241,0.06)",
    border: "rgba(99,102,241,0.30)",
    iconBg: "rgba(99,102,241,0.12)",
    labelColor: "#4338ca",
    labelBg: "rgba(99,102,241,0.12)",
    accent: "#6366f1",
    tabBg: "rgba(99,102,241,0.10)",
    tabText: "#3730a3",
  },
  rupture: {
    icon: "🚨",
    label: "Rupture de stock",
    cardBg: "rgba(239,68,68,0.06)",
    border: "rgba(239,68,68,0.35)",
    iconBg: "rgba(239,68,68,0.12)",
    labelColor: "#b91c1c",
    labelBg: "rgba(239,68,68,0.12)",
    accent: "#ef4444",
    tabBg: "rgba(239,68,68,0.10)",
    tabText: "#991b1b",
  },
  insuffisant: {
    icon: "⚠️",
    label: "Stock insuffisant",
    cardBg: "rgba(249,115,22,0.06)",
    border: "rgba(249,115,22,0.35)",
    iconBg: "rgba(249,115,22,0.12)",
    labelColor: "#c2410c",
    labelBg: "rgba(249,115,22,0.12)",
    accent: "#f97316",
    tabBg: "rgba(249,115,22,0.10)",
    tabText: "#9a3412",
  },
  default: {
    icon: "🔔",
    label: "Notification",
    cardBg: "rgba(148,163,184,0.07)",
    border: "rgba(148,163,184,0.30)",
    iconBg: "rgba(148,163,184,0.14)",
    labelColor: "#475569",
    labelBg: "rgba(148,163,184,0.16)",
    accent: "#94a3b8",
    tabBg: "rgba(148,163,184,0.10)",
    tabText: "#334155",
  },
};

const tabConfig = [
  { key: "all",       label: "Tout",        color: { bg: "#f1f5f9", text: "#0f172a", accent: "#0f172a" } },
  { key: "confirmer", label: "À confirmer", color: { bg: typeConfig.confirmer.tabBg, text: typeConfig.confirmer.tabText, accent: typeConfig.confirmer.accent } },
  { key: "reporte",   label: "Reportées",   color: { bg: typeConfig.reporte.tabBg,   text: typeConfig.reporte.tabText,   accent: typeConfig.reporte.accent   } },
  { key: "stock",     label: "Stock",       color: { bg: typeConfig.rupture.tabBg,   text: typeConfig.rupture.tabText,   accent: typeConfig.rupture.accent   } },
];

const detectType = (notif) => {
  if (notif.etat_tache === 3) return "reporte";
  if (notif.catache) return "confirmer";
  return "default";
};

const buildMessages = (messageArray, profilId) => {
  if (!messageArray || !Array.isArray(messageArray)) return [];
  return messageArray.map((singleNotif) => {
    const type = detectType(singleNotif);
    let text = "";
    if (profilId === 1) {
      text = `Nouvelle tâche "${singleNotif.cataches?.catache}" exécutée par ${singleNotif.users?.name}`;
    } else if (profilId === 2 && type === "reporte") {
      text = `Tâche "${singleNotif.catache}" de ${singleNotif.nomComplet} reportée par ${singleNotif.user_name}`;
    } else if (profilId === 2) {
      text = `Confirmation "${singleNotif.catache}" envoyée par ${singleNotif.user_name} pour ${singleNotif.nomComplet}`;
    }
    return {
      id: singleNotif.id,
      message: text,
      messageDate: singleNotif.date_affectation,
      isNew: singleNotif.NotifificationStatus === 1,
      type,
      tab: "tache",
    };
  });
};

const buildStockMessages = (stockData) => {
  if (!stockData || !Array.isArray(stockData)) return [];
  return stockData.map((item, index) => ({
    id: `stock-${item.user_id}-${item.type_appareil_id}-${index}`,
    message: `${item.user_name} — ${item.type_appareil_nom}`,
    messageDate: null,
    isNew: true,
    type: item.stock_status === "rupture" ? "rupture" : "insuffisant",
    tab: "stock",
    stockStatus: item.stock_status,
    nombreAppareils: item.nombre_appareils,
    typeAppareilNom: item.type_appareil_nom,
    userName: item.user_name,
  }));
};


const Notification = ({
  status,
  message,
  notificationForST,
  getTaskTech,
  stockData    = [],   
  stockLoading = false,
  fetchStock,          
}) => {
  const { USE } = useContext(AuthContext);

  const [showed, setShowed]       = useState(false);
  const [hovered, setHovered]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [messages, setMessages]   = useState([]);
  const [dismissedStock, setDismissedStock] = useState(new Set());
  const [activeTab, setActiveTab] = useState("all");
  const panelRef = useRef(null);

  /* ── Tâche notifications ── */
  useEffect(() => {
    if (!message || !Array.isArray(message) || message.length === 0) return;
    setMessages(buildMessages(message, USE.profil_id));
    setLoading(true);
  }, [message, status]);

  /* ── Stock messages dérivés de la prop stockData ── */
  const stockMessages = buildStockMessages(stockData).filter(
    (m) => !dismissedStock.has(m.id)
  );

  /* ── Close on outside click ── */
  useEffect(() => {
    const handleOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowed(false);
      }
    };
    if (showed) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showed]);

  const handleShow = async () => {
    // if (!showed) {
    //   if (USE.profil_id === 2) notificationForST();
    //   else if (USE.profil_id === 1) getTaskTech();
    // }
    setShowed((s) => !s);
  };

  /* ── Dismiss ── */
  const remove = async (e, id) => {
    e.stopPropagation();
    if (String(id).startsWith("stock-")) {
      setDismissedStock((prev) => new Set([...prev, id]));
      return;
    }
    await axios.post(`/api/technicien/NotificationUpdate/${id}`);
    setMessages((prev) => prev.filter((m) => m.id !== id));
    await notificationForST?.();
  };

  /* ── Filtered views ── */
  const allMessages = [...messages, ...stockMessages];
  const filtered =
    activeTab === "all"   ? allMessages :
    activeTab === "stock" ? stockMessages :
    messages.filter((m) => m.type === activeTab);

  const newCount   = allMessages.filter((m) => m.isNew).length;
  const stockCount = stockMessages.length;
  const totalStatus = (Number(status) || 0) + stockCount;

  const tabCount = (key) => {
    if (key === "all")   return allMessages.length;
    if (key === "stock") return stockMessages.length;
    return messages.filter((m) => m.type === key).length;
  };

  return (
    <div ref={panelRef} style={{ position: "relative", display: "inline-block", fontFamily: "'DM Sans', sans-serif" }}>

      <div
        style={{
          position: "relative", width: 44, height: 44, borderRadius: "50%",
          border: "none", cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center",
          background: hovered ? "rgba(99,102,241,0.10)" : "transparent",
          transition: "background 0.2s",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleShow}
      >
        {totalStatus > 0 && (
          <span style={{
            position: "absolute", top: 4, right: 4,
            minWidth: 18, height: 18, borderRadius: 9,
            background: "linear-gradient(135deg,#f43f5e,#e11d48)",
            color: "#fff", fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px", boxShadow: "0 2px 6px rgba(244,63,94,0.45)",
            animation: "badgePop 0.3s cubic-bezier(0.34,1.56,0.64,1) both",
          }}>
            {totalStatus > 999 ? "999+" : totalStatus}
          </span>
        )}
        <svg width="22" height="22" viewBox="0 0 24 24"
          fill={hovered ? "#6366f1" : "#64748b"}
          style={{ transition: "fill 0.2s" }}>
          <path d="M12 24c-1.66 0-3-1.34-3-3h6c0 1.66-1.34 3-3 3zm7-10.83c1.58 1.52 2.67 3.55 3 5.83H2c.33-2.28 1.42-4.31 3-5.83V7c0-3.87 3.13-7 7-7s7 3.13 7 7v6.17z" />
        </svg>
      </div>

      {/* ── Panel ── */}
      {showed && (
        <div style={{
          position: "fixed", top: 68, right: 16, width: 400,
          maxHeight: "calc(100vh - 90px)", borderRadius: 20, background: "#fff",
          boxShadow: "0 8px 40px rgba(17,24,39,0.13), 0 1.5px 6px rgba(17,24,39,0.07)",
          display: "flex", flexDirection: "column", zIndex: 1000, overflow: "hidden",
          animation: "slideDown 0.22s cubic-bezier(0.34,1.56,0.64,1) both",
        }}>

          {/* Header */}
          <div style={{
            flexShrink: 0, padding: "20px 22px 14px", borderBottom: "1px solid #f1f5f9",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>🔔</span>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", margin: 0, letterSpacing: "-0.3px" }}>
                Notifications
              </h2>
              {allMessages.length > 0 && (
                <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 8, fontSize: 12, fontWeight: 600, padding: "2px 8px" }}>
                  {allMessages.length}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {stockCount > 0 && (
                <span style={{
                  display: "flex", alignItems: "center", gap: 4,
                  fontSize: 11, fontWeight: 700, color: "#b91c1c",
                  background: "rgba(239,68,68,0.10)", borderRadius: 7, padding: "3px 8px",
                }}>
                  📦 {stockCount} alerte{stockCount > 1 ? "s" : ""}
                </span>
              )}
              {newCount > 0 && (
                <span style={{ fontSize: 12, color: "#6366f1", fontWeight: 600 }}>
                  {newCount} nouvelles
                </span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ flexShrink: 0, display: "flex", padding: "10px 16px 0", gap: 4, borderBottom: "1px solid #f1f5f9", overflowX: "auto" }}>
            {tabConfig.map((t) => {
              const count    = tabCount(t.key);
              const isActive = activeTab === t.key;
              return (
                <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                  padding: "6px 12px", borderRadius: "10px 10px 0 0", whiteSpace: "nowrap",
                  fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none",
                  background: isActive ? t.color.bg : "transparent",
                  color: isActive ? t.color.text : "#94a3b8",
                  transition: "all 0.15s",
                  borderBottom: isActive ? `2px solid ${t.color.accent}` : "2px solid transparent",
                }}>
                  {t.label}
                  {t.key !== "all" && (
                    <span style={{
                      marginLeft: 5, borderRadius: 6, fontSize: 10, padding: "1px 6px", fontWeight: 700,
                      background: isActive ? t.color.accent : "#e2e8f0",
                      color: isActive ? "#fff" : "#94a3b8",
                      transition: "all 0.15s",
                    }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* List */}
          <div className="notif-list" style={{
            flex: 1, minHeight: 0, overflowY: "auto",
            padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: 7,
          }}>
            {/* Loading states */}
            {activeTab === "stock" && stockLoading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "50px 0" }}>
                <MDBSpinner role="status" style={{ color: "#ef4444" }} />
              </div>
            ) : (!loading && activeTab !== "stock") ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "50px 0" }}>
                <MDBSpinner role="status" style={{ color: "#6366f1" }} />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: "50px 20px", color: "#94a3b8", fontSize: 13 }}>
                <span style={{ fontSize: 40, opacity: 0.35 }}>
                  {activeTab === "stock" ? "📦" : "📭"}
                </span>
                <span>{activeTab === "stock" ? "Aucune alerte de stock" : "Aucune notification ici"}</span>
              </div>
            ) : (
              filtered.map((n, i) => {
                const t       = typeConfig[n.type] || typeConfig.default;
                const isStock = n.tab === "stock";
                return (
                  <div
                    key={n.id}
                    className="notif-card"
                    onClick={(e) => remove(e, n.id)}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 12,
                      padding: "13px 14px", borderRadius: 14,
                      background: n.isNew ? t.cardBg : "#f8fafc",
                      border: `1.5px solid ${n.isNew ? t.border : "#e2e8f0"}`,
                      cursor: "pointer", flexShrink: 0,
                      transition: "transform 0.15s, box-shadow 0.15s",
                      animation: `fadeIn 0.2s ease ${i * 0.04}s both`,
                    }}
                  >
                    <div style={{
                      width: 38, height: 38, minWidth: 38, borderRadius: 12,
                      background: t.iconBg, display: "flex",
                      alignItems: "center", justifyContent: "center", fontSize: 17,
                    }}>
                      {t.icon}
                    </div>

                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
                      <span style={{
                        alignSelf: "flex-start", fontSize: 10, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.07em",
                        color: t.labelColor, background: t.labelBg,
                        borderRadius: 5, padding: "2px 7px",
                      }}>
                        {t.label}
                      </span>

                      <p style={{
                        margin: 0, fontSize: 13, color: "#1e293b", lineHeight: 1.55,
                        fontWeight: 500, whiteSpace: "normal", wordBreak: "break-word",
                      }}>
                        {n.message}
                      </p>

                      {isStock && (
                        <span style={{
                          alignSelf: "flex-start",
                          fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                          background: n.stockStatus === "rupture" ? "rgba(239,68,68,0.12)" : "rgba(249,115,22,0.12)",
                          color:      n.stockStatus === "rupture" ? "#b91c1c" : "#c2410c",
                        }}>
                          {n.stockStatus === "rupture"
                            ? "0 appareil en stock"
                            : `${n.nombreAppareils} appareil${n.nombreAppareils > 1 ? "s" : ""} restant${n.nombreAppareils > 1 ? "s" : ""}`}
                        </span>
                      )}

                      {!isStock && n.messageDate && (
                        <span style={{ fontSize: 11, color: "#94a3b8", display: "flex", alignItems: "center", gap: 4 }}>
                          🕐 {n.messageDate}
                        </span>
                      )}
                    </div>

                    {n.isNew && (
                      <div style={{
                        width: 7, height: 7, minWidth: 7, borderRadius: "50%",
                        background: isStock
                          ? (n.stockStatus === "rupture" ? "#ef4444" : "#f97316")
                          : "#6366f1",
                        marginTop: 5,
                      }} />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {allMessages.length > 0 && (
            <div style={{ flexShrink: 0, padding: "10px 22px 14px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "center", gap: 12 }}>
              <button
                className="notif-markall"
                style={{ background: "none", border: "none", color: "#6366f1", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "6px 12px", borderRadius: 8 }}
              >
                ✓ Tout marquer comme lu
              </button>
              {/* Refresh stock depuis le panel */}
              {fetchStock && (
                <button
                  onClick={fetchStock}
                  style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "6px 12px", borderRadius: 8 }}
                >
                  ↻ Actualiser stock
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Notification;
